import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import gsap from 'gsap';
import VastuCompass from '../3d/VastuCompass';
import { useContent } from '../../hooks/useContent';

export default function Hero() {
  const { content } = useContent();
  const h = content?.hero;
  const symbolsRef = useRef<(HTMLSpanElement | null)[]>([]);
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const floatingSymbols = h?.floatingSymbols ?? [
    { sym: 'ॐ', x: '8%', y: '20%', size: 32 },
    { sym: '☸', x: '92%', y: '15%', size: 26 },
    { sym: '✦', x: '5%', y: '70%', size: 18 },
    { sym: 'ॐ', x: '88%', y: '65%', size: 22 },
    { sym: '✦', x: '50%', y: '88%', size: 16 },
    { sym: '☸', x: '20%', y: '85%', size: 14 },
  ];
  const highlights = h?.highlights ?? ['Certified Consultant', 'Result-Oriented', 'Personalized Solutions'];
  const directions = h?.compassDirections ?? ['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW'];

  const dirPositions: React.CSSProperties[] = [
    { top: '2%', left: '50%', transform: 'translateX(-50%)' },
    { bottom: '2%', left: '50%', transform: 'translateX(-50%)' },
    { top: '50%', right: '2%', transform: 'translateY(-50%)' },
    { top: '50%', left: '2%', transform: 'translateY(-50%)' },
    { top: '10%', right: '10%' },
    { top: '10%', left: '10%' },
    { bottom: '10%', right: '10%' },
    { bottom: '10%', left: '10%' },
  ];

  useEffect(() => {
    symbolsRef.current.forEach((el, i) => {
      if (!el) return;
      gsap.to(el, { y: -25, duration: 2.5 + i * 0.5, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: i * 0.4 });
      gsap.to(el, { rotation: i % 2 === 0 ? 15 : -15, duration: 4 + i * 0.3, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: i * 0.2 });
    });
    if (bgRef.current) gsap.to(bgRef.current, { opacity: 0.7, duration: 3, ease: 'sine.inOut', yoyo: true, repeat: -1 });
  }, [content]);

  return (
    <section ref={sectionRef} style={{
      padding: '160px 0 100px', position: 'relative',
      background: 'linear-gradient(135deg, #0D0D1F 0%, #1A0F2E 50%, #0D0D1F 100%)',
      overflow: 'hidden'
    }}>
      <motion.div ref={bgRef as any} style={{
        position: 'absolute', inset: 0, y: bgY,
        backgroundImage: `radial-gradient(circle at 30% 50%, rgba(232,135,26,0.1) 0%, transparent 60%),
          radial-gradient(circle at 70% 30%, rgba(201,84,122,0.07) 0%, transparent 50%)`,
      }} />

      <motion.div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}
        animate={{ rotate: 360 }} transition={{ duration: 60, ease: 'linear', repeat: Infinity }}>
        <svg width="700" height="700" viewBox="0 0 700 700" opacity={0.03}>
          <circle cx="350" cy="350" r="320" stroke="#E8871A" strokeWidth="1" fill="none" />
          <circle cx="350" cy="350" r="240" stroke="#E8871A" strokeWidth="1" fill="none" />
          <polygon points="350,60 610,490 90,490" stroke="#E8871A" strokeWidth="1" fill="none" />
          <polygon points="350,640 90,210 610,210" stroke="#C9547A" strokeWidth="1" fill="none" />
        </svg>
      </motion.div>

      <motion.div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}
        animate={{ rotate: -360 }} transition={{ duration: 40, ease: 'linear', repeat: Infinity }}>
        <svg width="400" height="400" viewBox="0 0 400 400" opacity={0.05}>
          <circle cx="200" cy="200" r="180" stroke="#E8871A" strokeWidth="1" strokeDasharray="8 6" fill="none" />
          {[0,45,90,135,180,225,270,315].map((a, i) => (
            <line key={i}
              x1={200 + 160 * Math.cos(a * Math.PI / 180)} y1={200 + 160 * Math.sin(a * Math.PI / 180)}
              x2={200 + 180 * Math.cos(a * Math.PI / 180)} y2={200 + 180 * Math.sin(a * Math.PI / 180)}
              stroke="#E8871A" strokeWidth="2" />
          ))}
        </svg>
      </motion.div>

      {floatingSymbols.map((s: any, i: number) => (
        <span key={i} ref={el => { symbolsRef.current[i] = el; }} style={{
          position: 'absolute', left: s.x, top: s.y, fontSize: s.size,
          color: '#E8871A', opacity: 0.18, fontFamily: 'serif',
          pointerEvents: 'none', userSelect: 'none', display: 'inline-block'
        }}>{s.sym}</span>
      ))}

      <motion.div style={{ y: contentY, opacity }}>
        <div className="container hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }}>
          <div>
            <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.6 }} className="section-eyebrow">
              {h?.eyebrow ?? 'Authentic Vastu · Practical Solutions'}
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: 'easeOut' }}
              style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', lineHeight: 1.15, marginBottom: 20 }}>
              {h?.headline ?? 'Align Your Space,'}<br />
              <motion.span style={{ color: '#E8871A', display: 'inline-block' }}
                animate={{ textShadow: ['0 0 20px rgba(232,135,26,0)', '0 0 30px rgba(232,135,26,0.4)', '0 0 20px rgba(232,135,26,0)'] }}
                transition={{ duration: 3, repeat: Infinity }}>
                {h?.headlineHighlight ?? 'Transform Your Life'}
              </motion.span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              style={{ fontSize: 16, lineHeight: 1.8, opacity: 0.75, marginBottom: 32, maxWidth: 480 }}>
              {h?.description ?? 'Practical, result-oriented Vastu consultations for homes, offices, factories, and commercial spaces.'}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
              style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Link to={h?.cta?.primary?.path ?? '/contact'} className="btn-primary">{h?.cta?.primary?.label ?? 'Book Consultation'}</Link>
              <Link to={h?.cta?.secondary?.path ?? '/services'} className="btn-outline">{h?.cta?.secondary?.label ?? 'Our Services'}</Link>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
              style={{ display: 'flex', gap: 32, marginTop: 48, flexWrap: 'wrap' }}>
              {highlights.map((s: string, i: number) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 + i * 0.15 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#E8871A' }}>✦</span>
                  <p style={{ fontSize: 13, opacity: 0.7, letterSpacing: 0.5 }}>{s}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div className="hero-compass" initial={{ opacity: 0, scale: 0.7, rotate: -10 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1.2, ease: 'easeOut' }} style={{ height: 480, position: 'relative' }}>
            <VastuCompass />
            {directions.map((label: string, i: number) => (
              <motion.span key={i} initial={{ opacity: 0 }} animate={{ opacity: 0.8 }} transition={{ delay: 1.2 + i * 0.1 }}
                style={{ position: 'absolute', ...dirPositions[i], fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#E8871A', fontWeight: 600, letterSpacing: 1, pointerEvents: 'none' }}>
                {label}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, opacity: 0.45 }}>
        <span style={{ fontSize: 11, letterSpacing: 3, fontFamily: 'Inter, sans-serif' }}>SCROLL</span>
        <motion.div animate={{ scaleY: [0, 1, 0], opacity: [0, 1, 0] }} transition={{ duration: 1.8, repeat: Infinity }}
          style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, #E8871A, transparent)', transformOrigin: 'top' }} />
      </motion.div>

      <style>{`
        @media (max-width: 1024px) { .hero-grid { grid-template-columns: 1fr !important; } .hero-compass { display: none !important; } }
        @media (max-width: 768px) { .hero-grid { grid-template-columns: 1fr !important; gap: 24px !important; } .hero-compass { display: none !important; } }
      `}</style>
    </section>
  );
}
